/**
 * What the buyer is shown before paying, worked out the way the charge is.
 *
 * Every checkout surface (the order summary, the Pay button, the receipt)
 * prints item, shipping, processing fee and total. They used to each do their
 * own sum, in dollars, some truncating the fee and some rounding it, and none
 * of them knew about an automatic coupon: /api/listings/[id] hands the page the
 * sale price with the coupon already off, so checkout printed "Listing Price
 * $14" for an item the product page had just shown as "Original $20, 30% off,
 * Sale Price $14".
 *
 * This module is the one sum. It runs in cents through the same two functions
 * /api/checkout/create-payment-intent bills with (computeCheckoutCharge and,
 * for a code, the same subtraction applyBuyerDiscount makes), so the number on
 * the page is the number on the card.
 *
 * The rules it encodes, all of them set by what the server charges:
 *
 *  1. An automatic coupon shows as Original (struck), Discount, Sale Price.
 *     Nothing is sent for it: the server re-derives it from the listing.
 *  2. A code the buyer types is a coupon line under an undiscounted listing
 *     price. It is never drawn as a "discounted listing".
 *  3. Exactly one coupon moves the price. When a code is in play the server
 *     validates it against the ORIGINAL price and does not apply the automatic
 *     one, so the automatic discount disappears from the breakdown and the code
 *     comes off the original price.
 */

import {
  ListingAutoCouponFieldsSchema,
  type ListingAutoCouponFields,
} from '../../schemas/api/listings/get';
import { computeCheckoutCharge, toCents } from './create-payment-intent-decisions';

/** The listing's automatic coupon, in the units the display needs. */
export interface AutoCoupon {
  couponId: number;
  name: string;
  /** What the listing costs before the coupon. */
  originalPrice: number;
  /** What the coupon takes off, in dollars. */
  discountAmount: number;
  /** What the listing costs with it: the `price` the listing API returned. */
  salePrice: number;
  /** The Discount row, worded as the product page words it: "30% off". */
  label: string;
}

/**
 * The automatic coupon attached to a listing, or null when there is none.
 *
 * `price` from the listing API is the sale price, so the original is the sale
 * price plus the discount. That holds exactly: calculateDiscount never lets a
 * discount exceed the item price, so `Math.max(0, cost - discount)` in the
 * route never clamps. Summed in cents so 13.99 + 6.01 is 20, not 19.999…
 */
export function readAutoCoupon(listing: unknown): AutoCoupon | null {
  const parsed = ListingAutoCouponFieldsSchema.safeParse(listing);
  if (!parsed.success) return null;
  const fields: ListingAutoCouponFields = parsed.data;

  const saleCents = toCents(fields.price);
  const discountCents = toCents(fields.couponDiscountAmount);
  const originalCents = saleCents + discountCents;

  return {
    couponId: fields.couponId,
    name: fields.couponName || 'Automatic discount',
    originalPrice: originalCents / 100,
    discountAmount: discountCents / 100,
    salePrice: saleCents / 100,
    label: discountLabel(fields.percentOff, discountCents, originalCents),
  };
}

/**
 * "30% off": the product page prints the route's `percentOff`, rounded to a
 * whole percent, for fixed and percentage coupons alike. Worked out the same
 * way when the field is missing so the two pages cannot disagree.
 */
function discountLabel(percentOff: number | null | undefined, discountCents: number, originalCents: number): string {
  const percent = typeof percentOff === 'number' && percentOff > 0
    ? percentOff
    : Math.round((discountCents / originalCents) * 100);
  return `${percent}% off`;
}

/** The coupon state CheckoutProvider keeps for a code the buyer typed. */
export interface CodeCouponState {
  couponType: string | null | undefined;
  couponDiscount: number | null | undefined;
  couponCode?: string | null;
  /** 'code', or the legacy 'auto' (discount already inside the price), or empty. */
  couponApplicationMode: string | null | undefined;
}

export interface CheckoutPriceInput {
  /** The listing as /api/listings/[id] returned it: `price` plus any auto-coupon keys. */
  listing: unknown;
  /** Vehicles arrange shipping separately, so no rate is ever charged. */
  isVehicleListing: boolean;
  /** The highlighted rate's `amount`, in dollars. */
  shippingAmount?: number | string | null;
  /** The tax quoted with that rate, in cents. */
  taxCents?: number | null;
  coupon: CodeCouponState;
}

export interface CheckoutPriceBreakdown {
  /** The price before any coupon: the original when an automatic coupon exists. */
  listPrice: number;
  /** The automatic coupon when it is the one being charged; null otherwise. */
  autoCoupon: AutoCoupon | null;
  /** A typed code's discount, in dollars; 0 when no code is in play. */
  codeDiscount: number;
  codeLabel: string | null;
  /** What the item is billed at, after whichever coupon applies. */
  itemPrice: number;
  shipAmount: number;
  subTotal: number;
  stripeFee: number;
  salesTax: number;
  totalPrice: number;
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * The listing's price in cents, or null when there is no product yet or its
 * price cannot be read. A negative price counts as zero.
 */
function listingPriceCents(listing: unknown): number | null {
  const price = finiteNumber((listing as { price?: unknown } | null | undefined)?.price);
  return price === null ? null : Math.max(0, toCents(price));
}

/**
 * The full breakdown for one checkout.
 *
 * A code only counts when it is a buyer coupon with a positive discount:
 * a `seller` coupon lowers the seller's payout, never the buyer's bill. The
 * legacy `'auto'` application mode means the discount is already inside
 * `listing.price` and must not come off twice.
 */
export function checkoutPriceBreakdown(input: CheckoutPriceInput): CheckoutPriceBreakdown {
  const auto = readAutoCoupon(input.listing);
  const { coupon } = input;
  const codeDiscountDollars = coupon.couponType === 'buyer' ? Number(coupon.couponDiscount) || 0 : 0;
  const codeActive = codeDiscountDollars > 0 && coupon.couponApplicationMode !== 'auto';

  const plainCents = listingPriceCents(input.listing);
  // No readable price means no product yet: the page shows nothing owed,
  // shipping included, until it loads. The fee is still quoted off a zero
  // subtotal, which is how the page has always drawn that moment.
  const hasItem = auto !== null || plainCents !== null;

  let listCents: number;
  let itemCents: number;
  let codeCents = 0;
  if (codeActive) {
    // The server validates a code against the DB price, which is the original,
    // and applies it instead of the automatic coupon.
    listCents = auto ? toCents(auto.originalPrice) : plainCents ?? 0;
    // calculateDiscount never lets a coupon exceed the item, so neither does
    // the line that prints it.
    codeCents = Math.min(toCents(codeDiscountDollars), listCents);
    itemCents = listCents - codeCents;
  } else if (auto) {
    listCents = toCents(auto.originalPrice);
    itemCents = toCents(auto.salePrice);
  } else {
    listCents = plainCents ?? 0;
    itemCents = listCents;
  }

  const shippingDollars = input.isVehicleListing || !hasItem ? 0 : finiteNumber(input.shippingAmount) ?? 0;
  const shipCents = Math.max(0, toCents(shippingDollars));
  const charge = computeCheckoutCharge(itemCents, shipCents);

  const rawTax = input.isVehicleListing || !hasItem ? 0 : Number(input.taxCents ?? 0);
  const taxCents = Number.isFinite(rawTax) && rawTax > 0 ? Math.round(rawTax) : 0;

  return {
    listPrice: listCents / 100,
    autoCoupon: !codeActive && auto ? auto : null,
    codeDiscount: codeCents / 100,
    codeLabel: codeActive ? coupon.couponCode || null : null,
    itemPrice: itemCents / 100,
    shipAmount: shipCents / 100,
    subTotal: (itemCents + shipCents) / 100,
    stripeFee: charge.stripeFeeCents / 100,
    salesTax: taxCents / 100,
    totalPrice: (charge.totalCents + taxCents) / 100,
  };
}

/**
 * Whether a typed code should replace the listing's automatic coupon.
 *
 * The server applies one coupon, and a code replaces the automatic one. A code
 * worth no more than the automatic discount would raise the price above what
 * the page showed, which the server refuses ("The price of this item
 * changed"). So a code only takes over when it saves more; otherwise the
 * automatic discount stays and the buyer is told why.
 */
export function codeBeatsAutoCoupon(auto: AutoCoupon | null, code: { couponId?: number | null; discountAmount: number }): boolean {
  if (!auto) return true;
  if (code.couponId && code.couponId === auto.couponId) return false;
  return toCents(code.discountAmount) > toCents(auto.discountAmount);
}
